import type { Image3D } from '~/domain/patient/image3d';
import type { StructureSet } from '~/domain/patient/structureset';
import type { ConnectorPatientManifest } from '~/domain/persistence/ConnectorPatientManifest';
import type { OpenSelection } from './resolveOpenIntent';

/**
 * The loaded objects an open resolved to.
 *
 * The intent names datasets by their Connector id; the parsed DICOM knows
 * nothing about those. The manifest is the bridge — it crossed the seam with
 * the files and maps each dataset id to the UIDs the parsed objects carry.
 */
export type IntendedObjects = {
    image?: Image3D;
    /**
     * Registration only: the other side of the pair. The environment does not
     * choose one on its own, so without carrying it through, the step would
     * open with the second image retrieved but unselected.
     */
    secondaryImage?: Image3D;
    structureSet?: StructureSet;
};

/**
 * Matches the intent's dataset ids to what was actually loaded.
 *
 * Anything that does not match is simply absent, and the caller falls back to
 * its own default. That is deliberate: a resolution naming an object the
 * retrieval did not bring is a reason to open the patient anyway, not a reason
 * to open nothing.
 */
export function intendedObjects(
    intent: OpenSelection | undefined,
    manifest: ConnectorPatientManifest | undefined,
    loaded: {
        images: readonly Image3D[];
        structureSets: readonly StructureSet[];
    }
): IntendedObjects {
    if (intent === undefined || manifest === undefined) {
        return {};
    }

    const seriesUidOf = (datasetId: string | undefined): string | undefined =>
        datasetId
            ? manifest.images.find((entry) => entry.datasetId === datasetId)
                  ?.seriesInstanceUid
            : undefined;
    const imageOf = (datasetId: string | undefined): Image3D | undefined => {
        const seriesUid = seriesUidOf(datasetId);
        return seriesUid
            ? loaded.images.find((image) => image.seriesUid === seriesUid)
            : undefined;
    };

    const setSopUid = intent.structureSetId
        ? manifest.structureSets.find(
              (entry) => entry.datasetId === intent.structureSetId
          )?.sopInstanceUid
        : undefined;

    return {
        image: imageOf(intent.imageId),
        secondaryImage: imageOf(intent.secondaryImageId),
        // By SOP Instance UID, never by series: two structure sets can legally
        // share one Series UID, and picking the wrong one would put the user
        // in the right workflow on the wrong contours.
        structureSet: setSopUid
            ? loaded.structureSets.find((set) => set.dicomUid === setSopUid)
            : undefined,
    };
}
